import { useEffect, useState } from 'react';

import { Divider, Text, type TypeSelectOption, dialogService, toastService } from '@thefarmersfront/kitchen';

import type { IRowNode } from 'ag-grid-community';
import dayjs from 'dayjs';
import { useShallow } from 'zustand/react/shallow';

import {
  ClaimReturnDetailModal,
  ClaimReturnFilter,
  ClaimReturnRegisterModal,
  ClaimReturnTable,
} from '~/components/claim/return';
import { PageTitle } from '~/components/common';
import { PageWrapper } from '~/components/layout';

import { useDeleteReturnOrders, useGetReturnOrders, useGetReturnOrdersReturnReceiptId } from '~/hooks/claim';
import { useModal, useToggle } from '~/hooks/common';
import { useGetCodes } from '~/hooks/order';
import { useClaimStore, useUserInfoStore } from '~/store';
import { addIf, formatDate } from '~/utils';

import { BREAD_CRUMB, CLAIM, OPTION_ALL, PATH, TOAST_MESSAGE } from '~/constants';
import {
  CLAIM_RETURN_STATUS,
  INITIAL_CLAIM_PAGE_CURSOR,
  INITIAL_CLAIM_PAGE_SIZE,
  KEYWORD_TYPE,
  MAX_CLAIM_DATE_RANGE,
} from '~/constants/claim';
import { MAX_KEYWORD_COUNT } from '~/constants/order';

import type {
  TypeGetReturnOrdersParams,
  TypeReturnData,
  TypeReturnDetailData,
  TypeReturnSearchFilterOptions,
  TypeReturnSearchFilterValues,
  TypeReturnSearchFilterValuesState,
} from '~/types/claim.types';

const INITIAL_SEARCH_FILTER_VALUES: TypeReturnSearchFilterValues = {
  keywordType: null,
  keyword: '',
  returnDate: { start: new Date(), end: new Date() },
  returnStatus: OPTION_ALL,
  pagination: {
    currentPage: INITIAL_CLAIM_PAGE_CURSOR,
    totalCount: 0,
    pageSize: INITIAL_CLAIM_PAGE_SIZE,
  },
};

const ClaimReturnPage = () => {
  const { requestCodes } = useGetCodes();
  const { requestReturnOrders } = useGetReturnOrders();
  const { requestDeleteReturnOrders } = useDeleteReturnOrders();
  const { requestReturnOrdersDetail } = useGetReturnOrdersReturnReceiptId();

  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasPermissionNFA = hasPermission([{ ownerType: 'NFA' }]);

  const [selectOptions, setSelectOptions] = useClaimStore(
    useShallow((state) => [state.selectOptions, state.actions.setSelectOptions])
  );
  const searchFilterOptions: TypeReturnSearchFilterOptions = {
    keyword: selectOptions.returnKeyword,
    returnStatus: [OPTION_ALL, ...selectOptions.returnStatus],
  };

  const [searchFilterValues, setSearchFilterValues] = useState<TypeReturnSearchFilterValues>({
    ...INITIAL_SEARCH_FILTER_VALUES,
  });
  const [searchFilterValuesStatus, setSearchFilterValuesStatus] = useState<TypeReturnSearchFilterValuesState>({
    keyword: 'default',
    returnDate: 'default',
  });
  const [returnOrdersData, setReturnOrdersData] = useState<TypeReturnData[] | null>(null);
  const [selectedItem, setSelectedItem] = useState<TypeReturnData | null>(null);

  const [returnDetailData, setReturnDetailData] = useState<TypeReturnDetailData | null>(null);

  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });

  const returnDetailModal = useModal({
    onOpen: async (id?: string) => {
      if (!id) return;
      await requestReturnOrdersDetail(
        { returnReceiptId: id },
        {
          onSuccess: (data) => setReturnDetailData(data),
          onError: (error) => {
            toastService.addToast({
              type: 'error',
              message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
            });
          },
        }
      );
    },
  });

  const returnRegisterModal = useModal({
    onConfirmCompleted: () => handleSearch(),
  });

  useEffect(() => {
    requestCodes(
      {
        codeTypes: [
          'return-search-keyword',
          'return-status',
          'order-search-keyword',
          'order-search-date-type',
          'delivery-policy',
          'courier-code',
          'cluster-center',
          'pickup-type',
          'access-method',
          'return-reason',
        ],
      },
      {
        onSuccess: (codes) => {
          setSelectOptions({
            returnKeyword: codes.returnSearchKeyword,
            returnStatus: codes.returnStatus,
            orderKeyword: codes.orderSearchKeyword,
            orderDateType: codes.orderSearchDateType,
            deliveryPolicy: codes.deliveryPolicy,
            courierCode: codes.courierCode,
            clusterCenter: codes.clusterCenter,
            returnPlace: codes.pickUpType,
            accessMethod: codes.accessMethod,
            returnReason: codes.returnReason,
          });
          setSearchFilterValues({
            ...INITIAL_SEARCH_FILTER_VALUES,
            keywordType: codes.returnSearchKeyword[0] ?? null,
          });
        },
      }
    );
  }, []);

  const handleChangeFilterValues = (values: Partial<TypeReturnSearchFilterValues>) => {
    setSearchFilterValues((prev) => ({ ...prev, ...values }));
  };

  const handleChangeFilterValuesStatus = (status: Partial<TypeReturnSearchFilterValuesState>) => {
    setSearchFilterValuesStatus((prev) => ({ ...prev, ...status }));
  };

  const handleSearch = (filterValues: TypeReturnSearchFilterValues = searchFilterValues) => {
    if (
      filterValues.keywordType?.value === KEYWORD_TYPE.CLIENT_ORDER_CODE ||
      filterValues.keywordType?.value === KEYWORD_TYPE.OUTBOUND_ORDER_CODE ||
      filterValues.keywordType?.value === KEYWORD_TYPE.RETURN_RECEIPT_ID
    ) {
      if (filterValues.keyword.split(',').length > MAX_KEYWORD_COUNT) {
        dialogService.addDialog({
          children: `${filterValues.keywordType?.text}는 최대 ${MAX_KEYWORD_COUNT}개까지 검색할 수 있습니다.`,
        });
        setSearchFilterValuesStatus((prev) => ({ ...prev, keyword: 'error' }));
        return;
      }
    }

    const isDateRangeEmpty = !filterValues.returnDate?.start && !filterValues.returnDate?.end;
    const dateDiff = dayjs(filterValues.returnDate?.end).diff(filterValues.returnDate?.start, 'day');
    const isDateLimitExceeded = dateDiff >= MAX_CLAIM_DATE_RANGE;

    if (isDateRangeEmpty || isDateLimitExceeded) {
      dialogService.addDialog({
        children: `최대 ${MAX_CLAIM_DATE_RANGE}일 기간으로 입력 후 검색해주세요.`,
      });
      setSearchFilterValuesStatus((prev) => ({ ...prev, returnDate: 'error' }));
      return;
    }

    const keywordArr = filterValues.keyword
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    const params: TypeGetReturnOrdersParams = {
      ...addIf(
        filterValues.keywordType?.value === KEYWORD_TYPE.OUTBOUND_ORDER_CODE && keywordArr,
        'orderCodes',
        keywordArr
      ),
      ...addIf(
        filterValues.keywordType?.value === KEYWORD_TYPE.CLIENT_ORDER_CODE && keywordArr,
        'clientOrderCodes',
        keywordArr
      ),
      ...addIf(
        filterValues.keywordType?.value === KEYWORD_TYPE.RETURN_RECEIPT_ID && keywordArr,
        'returnReceiptIds',
        keywordArr
      ),
      ...addIf(
        filterValues.keywordType?.value === KEYWORD_TYPE.RECEIVER_NAME && keywordArr,
        'receiverName',
        filterValues.keyword
      ),
      receiptStartDate: formatDate(filterValues.returnDate?.start, 'YYYY-MM-DD') ?? '',
      receiptEndDate: formatDate(filterValues.returnDate?.end, 'YYYY-MM-DD') ?? '',
      returnReceiptStatuses: filterValues.returnStatus?.value ?? '',
      offset: filterValues.pagination.currentPage,
      limit: filterValues.pagination.pageSize,
    };

    requestReturnOrders(params, {
      onSuccess: ({ data, pagination }) => {
        setReturnOrdersData(data);
        setSelectedItem(null);

        setSearchFilterValues({
          ...filterValues,
          pagination: {
            ...filterValues.pagination,
            totalCount: pagination.totalCount,
          },
        });
      },
      onError: (error) => {
        if (error?.code === 2006) {
          setSearchFilterValuesStatus((prev) => ({ ...prev, returnDate: 'error' }));
          dialogService.addDialog({
            children: `최대 ${MAX_CLAIM_DATE_RANGE}일 기간으로 입력 후 검색해주세요.`,
          });
          return;
        }

        toastService.addToast({
          type: 'error',
          message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
        });
      },
    });
  };

  const handleResetFilterValues = () => {
    setSearchFilterValues({
      ...INITIAL_SEARCH_FILTER_VALUES,
      pagination: searchFilterValues.pagination,
      keywordType: searchFilterOptions.keyword[0] ?? null,
    });
    setSearchFilterValuesStatus({
      keyword: 'default',
      returnDate: 'default',
    });
  };

  const handleClickCancelReturn = () => {
    dialogService.addDialog({
      type: 'confirm',
      children: '반품 취소 시 즉시 처리됩니다.\n취소하시겠습니까?',
      confirmText: '반품 취소',
      onConfirm: handleCancelReturn,
    });
  };

  const handleCancelReturn = () => {
    if (selectedItem?.status.code !== CLAIM_RETURN_STATUS.RETURN_RECEIPT_COMPLETE.value) {
      dialogService.addDialog({
        children: (
          <>
            <Text weight="semi-bold" size="large" element="span">
              [회수예정]
            </Text>{' '}
            상태에서만 반품 취소가 가능합니다.
          </>
        ),
      });
      return;
    }

    requestDeleteReturnOrders(
      { returnReceiptId: selectedItem.returnReceiptId },
      {
        onSuccess: () => {
          toastService.addToast({
            message: '반품 취소되었습니다.',
          });
          handleSearch();
        },
        onError: (error) => {
          if (error?.code === CLAIM.ALREADY_RETURN_DELIVERY_ING) {
            dialogService.addDialog({
              children: '현재 배송사가 회수 진행중이므로\n반품 취소할 수 없습니다.',
            });
            return;
          }

          toastService.addToast({
            type: 'error',
            message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
          });
        },
      }
    );
  };

  const handleChangePageLimit = (value: TypeSelectOption<number>) => {
    handleSearch({
      ...searchFilterValues,
      pagination: {
        ...searchFilterValues.pagination,
        currentPage: INITIAL_CLAIM_PAGE_CURSOR,
        pageSize: value.value,
      },
    });
  };

  const handleChangePage = (value: number) => {
    handleSearch({
      ...searchFilterValues,
      pagination: {
        ...searchFilterValues.pagination,
        currentPage: value,
      },
    });
  };

  const handleChangeRowSelection = (selectedRows: IRowNode<TypeReturnData>[]) => {
    const selectedItem = selectedRows.map((row) => row.data).find((item) => !!item);
    setSelectedItem(selectedItem ?? null);
  };

  return (
    <PageWrapper>
      <PageTitle
        title="반품 신청/조회"
        breadcrumbs={BREAD_CRUMB[PATH.CLAIM_RETURN]}
        description={
          hasPermissionNFA ? (
            <Text size="medium" color="tertiary">
              검수 성공 시 반품 환불 처리는 네이버 판매자센터를 통해 진행하실 수 있습니다.
            </Text>
          ) : undefined
        }
        handleFilterToggle={handleFilterToggle}
      />
      {isFilterOpen && (
        <>
          <ClaimReturnFilter
            options={searchFilterOptions}
            values={searchFilterValues}
            valuesState={searchFilterValuesStatus}
            onChange={handleChangeFilterValues}
            onChangeStatus={handleChangeFilterValuesStatus}
            onSearch={() =>
              handleSearch({
                ...searchFilterValues,
                pagination: {
                  ...searchFilterValues.pagination,
                  currentPage: INITIAL_CLAIM_PAGE_CURSOR,
                },
              })
            }
            onReset={handleResetFilterValues}
          />
          <Divider color="gray100" />
        </>
      )}
      <ClaimReturnTable
        data={returnOrdersData}
        pagination={searchFilterValues.pagination}
        selectedItem={selectedItem}
        onChangePageLimit={handleChangePageLimit}
        onChangePage={handleChangePage}
        onChangeRowSelection={handleChangeRowSelection}
        onClickCancelReturn={handleClickCancelReturn}
        onOpenClaimReturnModal={returnRegisterModal.handleOpenModal}
        onClickReturnReceiptId={(id) => returnDetailModal.handleOpenModal(id)}
      />
      {returnDetailModal.isOpen && (
        <ClaimReturnDetailModal
          data={returnDetailData}
          isOpen={returnDetailModal.isOpen}
          onClose={returnDetailModal.handleCloseModal}
        />
      )}
      {returnRegisterModal.isOpen && (
        <ClaimReturnRegisterModal
          isOpen={returnRegisterModal.isOpen}
          onCancel={returnRegisterModal.handleCloseModal}
          onConfirm={returnRegisterModal.handleConfirmModal}
        />
      )}
    </PageWrapper>
  );
};

export default ClaimReturnPage;
